// ═══════════════════════════════════════════════════════════════════════════
//  CHART LANE — the single place where KALI and DUMABATI differ (God 2026-08-26)
//
//  God's order: "SuperChart ko KALI live trading aur DUMABATI paper trading ko
//  alag kiya jaye... dono alag hongi, ek doosre se koi connection nahi hogi."
//
//  WHAT WAS ACTUALLY WRONG. There was one SuperChart with no lane concept at
//  all, and its auto-fire hardcoded `${API}/webhook/tv` — the DUMABATI/paper
//  webhook on 5001. Verified live before writing this:
//
//    5001 /kali/webhook/tv    404   paper process does not serve KALI's path
//    PUBLIC /kali/webhook/tv  404   nginx does not route it
//    jaiswal-api TRADING_MODE paper guard_real_order blocks every real order
//    kali-executor KALI_LANE  live  /kali/webhook/tv registered only there
//
//  So no real money could leak — the dangerous direction was already safe, by
//  the structural isolation kali_webhook.py describes ("a misdirected KALI alert
//  gets a clean 404 rather than being quietly handled by the wrong lane"). But
//  the reverse was equally true: the chart could not reach KALI at all, and the
//  day a bot is promoted to LIVE that same chart would have fired it into the
//  PAPER webhook. That is the connection this module removes.
//
//  EVERY lane difference lives here. A difference that lives in one place cannot
//  be half-applied by the next person who touches the chart, and the guard
//  (tools/check_chart_lane_isolation.py) checks that nothing bypasses it.
// ═══════════════════════════════════════════════════════════════════════════
import { API_BASE_URL } from '../../liveTrading/foundation';

export type ChartLane = 'paper' | 'kali';

/** Storage is namespaced, never shared: two charts, two saved states. */
export const laneKey = (lane: ChartLane, key: string): string =>
  lane === 'kali' ? `${key}__kali` : key;

/**
 * The fire endpoint for this lane. There is deliberately NO fallback: if KALI's
 * path is unreachable the signal fails loudly and stops. Falling back to the
 * paper webhook is the exact contamination God asked to remove, and a silent
 * fallback is worse than a failure because nobody would ever see it.
 */
export const laneFireUrl = (lane: ChartLane): string =>
  lane === 'kali' ? `${API_BASE_URL}/kali/webhook/tv` : `${API_BASE_URL}/webhook/tv`;

/**
 * Human label used in banners, toasts and the fire log. Worded exactly like the
 * sidebar tabs (God 2026-08-26) — the chart, the tab and the log must all call
 * the same lane by the same name, or they read as three different things.
 */
export const laneLabel = (lane: ChartLane): string =>
  lane === 'kali' ? 'KALI SUPER CHART · LIVE TRADING' : 'DUMABATI SUPER CHART · PAPER TRADING';

/**
 * May a bot with this tradingMode be fired from a chart in this lane?
 *
 * An absent mode counts as PAPER. That default is deliberate and matches the
 * LiveTrading lane filter: an unlabelled bot must never be assumed to be
 * spending money, so it can never be fired from the real-money chart.
 */
export const botAllowedInLane = (tradingMode: unknown, lane: ChartLane): boolean => {
  const mode = String(tradingMode || 'PAPER').toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER';
  return mode === (lane === 'kali' ? 'LIVE' : 'PAPER');
};

/**
 * What the KALI page should DISPLAY for a bot — which is a different question
 * from whether that bot may FIRE (botAllowedInLane, above, still owns that).
 *
 * God 2026-08-28: "KALI LIVE TRADING ka poora design aur layout exactly PAPER
 * (Dumabati) jaisa hi complete aana chahiye. Jo bots aur symbols Paper me hain,
 * wahi Live me jayenge ... jab tak Pine V6 approve nahi hota, status WAITING FOR
 * PINE V6 APPROVAL dikhao ... bas unki live execution ruki rahegi."
 *
 * THIS IS NOT A REVERT of the 2026-08-26 lane fix, and the difference matters.
 * What God called zero-tolerance that day was the KALI page rendering paper bots
 * with RUNNING/PAPER badges and paper P&L — the real-money page CLAIMING paper
 * activity as its own. Hiding the bots was one way to stop that claim; it was
 * not the only one, and it cost the operator the fleet view. The claim is what
 * was wrong, so the claim is what this fixes: every bot is listed, and one that
 * is not LIVE-approved is labelled PENDING instead of borrowing a status it does
 * not have.
 *
 * Three things therefore stay exactly as they were, and none of them is display:
 *   - firing            botAllowedInLane still refuses a non-LIVE bot on KALI
 *   - money on screen   P&L, trades and open positions stay LIVE-only, so a
 *                       paper fill can never be totalled on the real-money page
 *   - the default       an absent tradingMode still counts as PAPER
 */
export type LaneBotView = {
  /** Draw this bot in this lane at all? */
  visible: boolean;
  /** Listed, but not cleared to trade here yet. */
  pending: boolean;
  /** Badge text for a pending bot; empty when it is not pending. */
  statusLabel: string;
};

export const PENDING_APPROVAL_LABEL = 'WAITING FOR PINE V6 APPROVAL';

export const laneBotView = (tradingMode: unknown, lane: ChartLane): LaneBotView => {
  const mode = String(tradingMode || 'PAPER').toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER';
  if (lane !== 'kali') {
    // DUMABATI is unchanged: it is the paper lane and shows the paper fleet.
    return { visible: mode === 'PAPER', pending: false, statusLabel: '' };
  }
  return mode === 'LIVE'
    ? { visible: true, pending: false, statusLabel: '' }
    : { visible: true, pending: true, statusLabel: PENDING_APPROVAL_LABEL };
};

/** Chrome, matching KaliSecurityPanel and MarketDepthPanel: KALI reads red. */
export const laneChrome = (lane: ChartLane) =>
  lane === 'kali'
    ? { accent: '#ef4444', accentDim: '#fca5a5', soft: 'rgba(239,68,68,.18)',
        border: 'rgba(239,68,68,.5)', badge: '🔱 KALI SUPER CHART · 🔴 LIVE TRADING' }
    : { accent: '#38bdf8', accentDim: '#7dd3fc', soft: 'rgba(56,189,248,.15)',
        border: 'rgba(56,189,248,.4)', badge: '🧪 DUMABATI SUPER CHART · PAPER TRADING' };

/**
 * Why a KALI fire failed, in words an operator can act on. KALI_WEBHOOK_TOKEN is
 * currently unset on the executor, so 5002 answers 503 — that is fail-closed
 * working as intended, not a bug to route around.
 */
export const laneFireError = (lane: ChartLane, status: number): string => {
  if (lane !== 'kali') return `signal POST failed (${status})`;
  if (status === 404) return 'KALI signal path is not routed yet — the alert was NOT sent, and was NOT sent to paper either.';
  if (status === 503) return 'KALI signal path is not ARMED (KALI_WEBHOOK_TOKEN unset) — God must set it. Nothing was sent to paper.';
  if (status === 401) return 'KALI webhook rejected the token — nothing was sent to paper.';
  return `KALI signal POST failed (${status}) — nothing was sent to paper.`;
};
